import { Store } from '@ngrx/store';
import { IHttpResponse, ILocationService, IRootScopeService, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import { ImNotification } from 'Src/ng2/shared/services/im-models/im-notification.service';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { Environments } from '../../../ng2/shared/constants/environments.constant';
import { RollbarMessages } from '../../../ng2/shared/constants/rollbar-messages.constant';
import { USER_ROLE_PERMISSIONS_FOR_STATES } from '../../../ng2/shared/constants/user-role-permissions-for-states.constant';
import { LoadTogglesSuccess } from '../../../ng2/store';
import { UpdatePreviousStates } from '../../../ng2/store/actions/previous-states-actions';
import { Auth } from './../../../ng2/shared/auth/auth.service';
import { ModalsService } from './../../../ng2/shared/modals/modals.service';
import { PrevStateService } from './../../../ng2/shared/services/prev-state/prev-state.service';
import { UserInactivity } from './../../../ng2/shared/services/user-inactivity/user-inactivity.service';
import { LoadCurrentUserSuccess } from './../../../ng2/store/actions/current-user-actions';

angular.module('nv.routes.main').config(configBlock);
/* istanbul ignore next */
function configBlock($stateProvider) {
  // define states
  $stateProvider.state('main', {
    // TODO inject stateProvider into spec, create new view that utlitizes this main abstract state and test that.
    abstract: true,
    views: {
      '': {
        template: require('../../layout/shell.html'),
        // TODO remove when confirmed to be no longer needed
        controller(
          publicConfig,
          $rootScope: IRootScopeService,
          $state: IStateService,
          PrevStateService: PrevStateService,
          Store: Store<any>,
        ) {
          switch (publicConfig.DISPLAY_NV_ENV) {
            case Environments.STAGING:
              this.isStaging = true;
              break;
            case Environments.DEV:
              this.isDev = true;
              break;
            case Environments.QA:
              this.isQa = true;
              break;
            case Environments.LOCAL:
              this.isLocal = true;
              break;
            case Environments.LION:
              this.isLion = true;
              break;
            case Environments.TIGER:
              this.isTiger = true;
              break;
            case Environments.BEAR:
              this.isBear = true;
              break;
            default:
              break;
          }

          $rootScope.$on('$stateChangeStart', () => {
            const opts = PrevStateService.getCurrentStateGoOpts($state);
            Store.dispatch(new UpdatePreviousStates(opts));
          });
        },
        controllerAs: '$ctrl',
      },
      'notifications@main': {
        template: `<notifications
            current-user="$resolve.currentUser"
            notifications="$resolve.notifications"
            ng-if="notificationsShellCtrl.showNotifications"
          >`,
        controller($rootScope: IRootScopeService, $state: IStateService, $timeout) {
          // ONLY show notifications in states that are children of `main.school`
          const PARENT_STATE_TO_SHOW_NOTIFICATIONS = 'main.school';

          const childOfParentState = $state.current.name.includes(PARENT_STATE_TO_SHOW_NOTIFICATIONS);
          this.showNotifications = childOfParentState ? true : false;

          $rootScope.$on('$stateChangeSuccess', (event, toState, toParams, fromState) => {
            const childOfParentState = toState.name.includes(PARENT_STATE_TO_SHOW_NOTIFICATIONS);

            // This is to set `showNotifications` after the digest cycle, to allow the url to be set so that
            // `NotificationMessageParser#parseMessage` can dynamically parse notifications
            // using the correct url (CM).
            $timeout(() => (this.showNotifications = childOfParentState ? true : false));
          });
        },
        // to prevent clashing with parent $ctrl we give controller another alias besides $ctrl (CM).
        controllerAs: 'notificationsShellCtrl',
      },
      'content@main': {
        template: '<div ui-view></div>',
      },
    },
    data: {
      roles: USER_ROLE_PERMISSIONS_FOR_STATES.main,
    },
    onEnter(
      PublicConfig,
      Rollbar,
      currentUser,
      ROLLBAR_BASE_CONFIG,
      UserAgentModal,
      $state,
      $location,
      ErrorModal,
      ImUser: ImUser,
    ) {
      // check that currentUser can access state directly using a url
      // this check is for going directly to a state using a url
      $state.transition.then(() => {
        const currentStateName = $state.current.name;
        if (currentStateName) {
          const landingOnSchoolRoutes = currentStateName.includes('main.school');
          if (landingOnSchoolRoutes) {
            // make sure the cluster user role type on school level gets recalced
            const schoolId = $state.params && $state.params.schoolId;
            currentUser.effectiveSchoolRoleType = ImUser.recalcRoleTypeOnSchoolLevel(currentUser, schoolId);
          }
          if (!ImUser.canAccessState(currentUser, currentStateName)) {
            const url = $location.$$path;
            const title = 'Unauthorized Access';
            const err = new Error(
              `You cannot access "${url}" because your role does not allow it. ` +
                `Please contact your school leader to request a role change.`,
            );
            err.name = RollbarMessages.INFO.UNAUTHORIZED_ACCESS;

            $state.go('main.home').then(() => ErrorModal.show(title, err));
          }
        }
      });

      UserAgentModal.checkBrowserType();

      const rollbarConfig = _.merge(ROLLBAR_BASE_CONFIG, {
        payload: {
          person: {
            id: currentUser._id, // required
            username: `${ImUser.getFullName(currentUser)}`,
            email: currentUser.authenticationEmail,
          },
        },
      });

      if (PublicConfig.ROLLBAR_POST_CLIENT_ITEM) {
        Rollbar.configure(rollbarConfig);
      }
    },
    resolve: {
      // we make sure that currentUser.whenProfileFetched resolves
      currentUser: [
        'AuthenticationRequiredError',
        '$rootScope',
        'ErrorModal',
        'Auth',
        'UserInactivity',
        'Rollbar',
        'User',
        '$window',
        'ImUser',
        'EventBus',
        'ModalsService',
        'SpinnerModal',
        function(
          AuthenticationRequiredError,
          $rootScope: IRootScopeService,
          ErrorModal,
          auth: Auth,
          userInactivity: UserInactivity,
          Rollbar,
          User,
          $window: IWindowService,
          ImUser: ImUser,
          EventBus,
          ModalsService: ModalsService,
          SpinnerModal,
        ) {
          let _currentUser;

          function checkStateAccess(toState, toParams, fromState) {
            // make sure the cluster user role type on school level gets recalced before calling ImUser.canAccessState - jchu
            const leavingSchoolRoutes = fromState.name.includes('main.school') && !toState.name.includes('main.school');
            const enteringSchoolRoutes =
              fromState.name.includes('main.school.home') && toState.name.includes('main.school');
            if (leavingSchoolRoutes) {
              _currentUser.effectiveSchoolRoleType = null;
            }
            if (enteringSchoolRoutes) {
              const schoolId = (toState.params && toState.params.schoolId) || toParams.schoolId;
              _currentUser.effectiveSchoolRoleType = ImUser.recalcRoleTypeOnSchoolLevel(_currentUser, schoolId);
            }
            if (!ImUser.canAccessState(_currentUser, toState.name)) {
              event.preventDefault();

              const fromStateName = fromState.name;
              const toStateName = toState.name;
              const title = 'Unauthorized Access';
              const err = new Error(
                `You cannot access this page because your role does not allow it. ` +
                  `Please contact your school leader to request a role change.`,
              );
              err.name = `Unauthorized Access from state "${fromStateName}" to "${toStateName}".`;
              ErrorModal.show(title, err);
            }
          }

          function getCurrentUser(currentUserId) {
            const currentUser = new User({ _id: currentUserId });
            return currentUser.$get();
          }

          function setCurrentUserSession(currentUser) {
            ImUser.setCurrentUserSession(currentUser);
            _currentUser = currentUser;
            return currentUser;
          }

          // redirects user
          function checkThatCaseloadUserHasStudents(currentUser) {
            return new Promise(resolve => {
              if (ImUser.isCaseloadUser(currentUser)) {
                const { cache } = currentUser;
                const hasCaseloadStudents = !!(cache && cache.caseloadStudentIds && cache.caseloadStudentIds.length);

                if (!hasCaseloadStudents) {
                  SpinnerModal.deactivateSpinner();

                  return ModalsService.openAlertModal({
                    title: 'No Assigned Students',
                    message: 'You have not been assigned any students. Please contact your school administrator.',
                    confirmText: 'SIGN OUT',
                  })
                    .afterClosed()
                    .subscribe(() => {
                      auth.logout();
                    });
                }
              }
              return resolve(currentUser);
            });
          }

          function setRollbarConfig(currentUser) {
            ImUser.setRollbarConfig(currentUser);
            return currentUser;
          }

          // check that currentUser can access state when transitioning between states
          // this check is for transitions within the app
          // close existing dialogs(modals) during the transition, reference: (jchu)
          // https://stackoverflow.com/questions/47723480/close-angular-material-dialog-on-browser-back)
          $rootScope.$on('$stateChangeStart', (event, toState, toParams, fromState) => {
            ModalsService.closeExistingModals();
            checkStateAccess(toState, toParams, fromState);
          });

          // TODO: Move this to its own service (CM)
          return auth
            .getCurrentUserId()
            .then(getCurrentUser)
            .then(user => {
              const currentUserAction = new LoadCurrentUserSuccess(user);
              EventBus.emitAction(currentUserAction);
              return user;
            })
            .then(setCurrentUserSession)
            .then(setRollbarConfig)
            .then(checkThatCaseloadUserHasStudents)
            .catch((error: IHttpResponse<any>) => {
              Rollbar.debug('Error fetching user db record', error);
              return Promise.reject(new AuthenticationRequiredError());
            });
        },
      ],
      notifications: [
        'Notification',
        'currentUser',
        'ImNotification',
        (Notification, currentUser, imNotification: ImNotification) => {
          const notifications = Notification.getNotificationsForUser().$promise;
          let results;
          return notifications.then(data => {
            results = _.compact(imNotification.filterForInProgress(data, currentUser._id));
            return results;
          });
        },
      ],
      // toggle state may need to be checked downstream
      toggles: [
        'ToggleService',
        'Store',
        '$location',
        (toggleService: ToggleService, store: Store<any>, $location: ILocationService) => {
          const url = $location.url();
          const isSchoolLevelRoute = url.includes('/school/');

          // We check to see if the url includes `/school/` to avoid calling `getAllComputedToggles` twice when going to the `main.school` child state.
          // There are some toggles that are school specific, so `getAllComputedToggles` also gets called in the `main.school` state with schoolId (Carlos).
          if (!isSchoolLevelRoute) {
            const toggleOption = {};
            return toggleService
              .getAllComputedToggles(toggleOption)
              .toPromise()
              .then(toggles => {
                const toggleAction = new LoadTogglesSuccess(toggles);
                store.dispatch(toggleAction);

                return toggles;
              })
              .catch(err => {
                return false;
              });
          }
        },
      ],
      publicConfig: [
        'PublicConfig',
        PublicConfig => {
          return PublicConfig;
        },
      ],
    },
  });
}
